import React, { useMemo, useState , useEffect } from "react";
import "./App.css";
import logo from "./assets/neurogreen-logo.png";
import config from "./config";
import ImageClassifier from "./components/ImageClassifier";
import PersonalReminders from "./components/PersonalReminders";

const TOWNSHIPS = [
  { id: "montgomery-nj", name: "Montgomery Township" },
  { id: "hillsborough-nj", name: "Hillsborough Township" },
];

const TOWNSHIP_STORAGE_KEY = "neurogreen_township_id";

function App() {
  const [activeSection, setActiveSection] = useState("chatbot");

  // Township selection (defaults to saved value or Montgomery)
  const [townshipId, setTownshipId] = useState(TOWNSHIPS[0].id);

  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [chatError, setChatError] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);  

  // Load saved township on first render
  useEffect(() => {
    try {
      const saved = localStorage.getItem(TOWNSHIP_STORAGE_KEY);
      if (saved && TOWNSHIPS.some((t) => t.id === saved)) {
        setTownshipId(saved);
      }
    } catch (_) {
      // ignore (private browsing / storage blocked)
    }
  }, []);

  // Persist township whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(TOWNSHIP_STORAGE_KEY, townshipId);
    } catch (_) {
      // ignore
    }
  }, [townshipId]);

  const sectionHelpText = useMemo(() => {
    switch (activeSection) {
      case "chatbot":
        return "Ask a recycling question and get guidance (ideally tailored to your town).";
      case "imageClassifier":
        return "Upload a photo of an item to find the right bin.";
      case "personalReminders":
        return "Set reminders so you never miss recycling pickup day.";
      default:
        return "";
    }
  }, [activeSection]);

  const handleNav = (section) => {
    setActiveSection(section);
    // UX: preserve user state when switching tabs (especially on mobile mis-taps)
    // If you DO want to clear, do it explicitly via a "Clear" button instead.
    setChatError("");
  };  

    const handleTownshipChange = (e) => {
    const next = e.target.value;
    setTownshipId(next);

    // Optional UX: clear chatbot output when switching townships (prevents “wrong-town” answers)
    setAnswer("");
    setChatError("");
  };

  // --- Chatbot Submit Handler ---
  const handleSubmit = async () => {
    if (!question.trim()) {
      setChatError("Type a question to get started.");
      setAnswer("");
      return;
    }

    setChatError("");
    setIsChatLoading(true);

    try {
      const response = await fetch(`${config.CHATBOT_API_URL}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "my_AI_service_api_key",
        },
        body: JSON.stringify({ question: question, townshipId: townshipId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAnswer(data.answer || "No answer available."); // API expected to return { answer: "..." }
    } catch (error) {
      console.error("Error:", error);
      setAnswer("");
      setChatError("Couldn’t reach the server. Please try again.");
    } finally {
      setIsChatLoading(false);
    }
  };

  const onChatKeyDown = (e) => {
    // Desktop UX: Enter submits. Mobile: Enter/Go submits.
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <div className="App">
      <header className="App-header">
        {/* Logo */}
        <img src={logo} className="App-logo" alt="NeuroGreen Logo" />

        {/* Title */}
        <h1 className="App-title">NeuroGreen</h1>

        {/* Punchline */}
        <p className="App-punchline">
          Revolutionizing Recycling with AI-Powered Solutions
        </p>

        {/* ✅ Township selector */}
        <div className="TownshipSelector">
          <label className="TownshipLabel" htmlFor="townshipSelect">
            Township
          </label>
          <select
            id="townshipSelect"
            className="TownshipSelect"
            value={townshipId}
            onChange={handleTownshipChange}
            aria-label="Select township"
          >
            {TOWNSHIPS.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>        

        {/* Tabs: better accessibility for desktop + mobile */}
        <nav className="App-buttons" role="tablist" aria-label="NeuroGreen features">
          <button
            type="button"
            className={`App-button ${activeSection === "chatbot" ? "is-active" : ""}`}
            role="tab"
            aria-selected={activeSection === "chatbot"}
            onClick={() => handleNav("chatbot")}
          >
            Chatbot
          </button>

          <button
            type="button"
            className={`App-button ${
              activeSection === "imageClassifier" ? "is-active" : ""
            }`}
            role="tab"
            aria-selected={activeSection === "imageClassifier"}
            onClick={() => handleNav("imageClassifier")}
          >
            Image Classifier
          </button>

          <button
            type="button"
            className={`App-button ${
              activeSection === "personalReminders" ? "is-active" : ""
            }`}
            role="tab"
            aria-selected={activeSection === "personalReminders"}
            onClick={() => handleNav("personalReminders")}
          >
            Personal Reminders
          </button>
        </nav>

        {/* Small microcopy helps first-time users (especially on mobile) */}
        <p className="App-sectionHelp" aria-live="polite">
          {sectionHelpText}
        </p>
      </header>
          {/* Main content outside header */}
      <main className="App-content">
        {activeSection === "chatbot" && (
          <section className="Chatbot-container" role="tabpanel">
            <label className="Chatbot-label" htmlFor="chatbotQuestion">
              Your question
            </label>

            <input
              id="chatbotQuestion"
              type="text"
              className="Chatbot-input"
              placeholder="Example: Can I recycle batteries in Montgomery Township?"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={onChatKeyDown}
              autoComplete="off"
              inputMode="text"
            />

            {/* Inline error is better than alert() for mobile/desktop */}
            {chatError && (
              <div className="Chatbot-error" role="alert">
                {chatError}
              </div>
            )}

            <div className="Chatbot-actions">
              <button
                type="button"
                className="Chatbot-submit"
                onClick={handleSubmit}
                disabled={isChatLoading}
              >
                {isChatLoading ? "Getting answer…" : "Ask NeuroGreen"}
              </button>

              <button
                type="button"
                className="Chatbot-clear"
                onClick={() => {
                  setQuestion("");
                  setAnswer("");
                  setChatError("");
                }}
                disabled={isChatLoading && !question && !answer}
              >
                Clear
              </button>
            </div>

            {answer && (
              <div className="Chatbot-answer" aria-live="polite">
                <h3>Answer</h3>
                <p>{answer}</p>
              </div>
            )}
          </section>
        )}

        {/* ✅ pass townshipId down so these components can call their APIs with it */}
        {activeSection === "imageClassifier" && (
          <section role="tabpanel">
            <ImageClassifier townshipId={townshipId} />
          </section>
        )}

        {activeSection === "personalReminders" && (
          <section role="tabpanel">
            <PersonalReminders townshipId={townshipId} />
          </section>
        )}
      </main>
    </div>
  );
}

export default App;
